import React from 'react';
import { Check } from 'lucide-react';
import SectionHeader from './SectionHeader';

const fallbackPackages = [
  {
    id: 'hobby',
    tier: 'Hobby',
    price: '0',
    desc: 'For side projects and experiments.',
    features: ['1 active deployment', 'Community support', 'ZIP & Docker deploy'],
    limits: { maxDeployments: 1, maxTeamMembers: 1, cicdEnabled: false, backupsEnabled: false },
  },
  {
    id: 'pro',
    tier: 'Pro',
    price: '29',
    desc: 'For indie builders shipping often.',
    features: ['Priority builds', 'Custom domains', 'Live terminal'],
    limits: { maxDeployments: 10, maxTeamMembers: 3, cicdEnabled: true, backupsEnabled: true },
  },
  {
    id: 'agency',
    tier: 'Pro+ Agency',
    price: '79',
    desc: 'For agencies managing client workspaces.',
    features: ['Team roles', 'Client isolation', 'Shared templates'],
    limits: { maxDeployments: 40, maxTeamMembers: 15, cicdEnabled: true, backupsEnabled: true },
  },
  {
    id: 'enterprise',
    tier: 'Enterprise',
    price: 'Custom',
    desc: 'For teams that need custom limits and support.',
    features: ['Dedicated support', 'Custom SLAs', 'SSO options'],
    limits: { maxDeployments: 10000, maxTeamMembers: 10000, cicdEnabled: true, backupsEnabled: true },
  },
];

const isPro = (pkg) =>
  pkg.id === 'pro' ||
  pkg.tierId === 'pro' ||
  (pkg.tier && String(pkg.tier).toLowerCase().includes('pro') && !String(pkg.tier).toLowerCase().includes('agency'));

const isEnterprise = (pkg) =>
  pkg.id === 'enterprise' ||
  pkg.tierId === 'enterprise' ||
  (pkg.tier && String(pkg.tier).toLowerCase().includes('enterprise'));

const isHobby = (pkg) =>
  pkg.id === 'hobby' ||
  pkg.tierId === 'hobby' ||
  (pkg.tier && String(pkg.tier).toLowerCase().includes('hobby'));

const PricingSection = ({ packages = [], onLoginClick }) => {
  const list = packages.length > 0 ? packages : fallbackPackages;

  return (
    <div className="lp-section lp-pricing">
      <div className="lp-container">
        <SectionHeader
          badge="Pricing"
          title="Simple, transparent pricing"
          subtitle="Start for free, scale when you need to."
        />

        <div className="lp-pricing-grid">
          {list.map((pkg, i) => {
            const popular = isPro(pkg);
            const enterprise = isEnterprise(pkg);
            const hobby = isHobby(pkg);
            const priceLabel = String(pkg.price).toLowerCase() === 'custom' ? 'Custom' : `$${pkg.price}`;

            return (
              <article key={pkg.id || i} className={`lp-price-card ${popular ? 'is-popular' : ''}`}>
                {popular ? <div className="lp-price-badge">Most popular</div> : null}
                <div className="lp-price-tier">{pkg.tier}</div>
                <div className="lp-price-amount">
                  {priceLabel}
                  {String(pkg.price).toLowerCase() !== 'custom' ? <span>/mo</span> : null}
                </div>
                <p className="lp-price-desc">{pkg.desc}</p>
                <ul className="lp-price-features">
                  {pkg.features
                    ?.filter((f) => !/deployment|team member|ci\/cd|backup/i.test(f))
                    .map((f, j) => (
                      <li key={j}><Check size={16} /> {f}</li>
                    ))}
                  {pkg.limits?.maxDeployments != null && (
                    <li>
                      <Check size={16} />
                      {pkg.limits.maxDeployments > 1000
                        ? 'Unlimited deployments'
                        : `Up to ${pkg.limits.maxDeployments} active deployments`}
                    </li>
                  )}
                  {pkg.limits?.maxTeamMembers != null && (
                    <li>
                      <Check size={16} />
                      {pkg.limits.maxTeamMembers > 1000
                        ? 'Unlimited team members'
                        : `Up to ${pkg.limits.maxTeamMembers} team members`}
                    </li>
                  )}
                  {pkg.limits?.cicdEnabled ? (
                    <li><Check size={16} /> GitHub/GitLab CI/CD</li>
                  ) : (
                    <li className="is-muted"><Check size={16} /> No CI/CD integration</li>
                  )}
                  {pkg.limits?.backupsEnabled ? (
                    <li><Check size={16} /> Automated backups</li>
                  ) : (
                    <li className="is-muted"><Check size={16} /> No automated backups</li>
                  )}
                </ul>
                <button
                  type="button"
                  className={`lp-btn ${popular ? 'lp-btn-primary' : 'lp-btn-secondary'} lp-btn-block`}
                  onClick={() => onLoginClick(pkg.id)}
                >
                  {enterprise ? 'Contact sales' : hobby ? 'Deploy for free' : 'Get started'}
                </button>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
